import { rgba } from 'polished';
import React, { useEffect, useRef } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import styled from 'styled-components';

import Box from '@ui/box/Box.tsx';
import Tooltip from '@ui/tooltip';

import BuildingRow from './BuildingRow.tsx';
import LevelsList from './LevelsList.tsx';

const AccordionStyledBoxShadowOverlay = styled(Box)`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    box-shadow:
      inset 0 18px 20px -20px ${rgba('#2e3032', 0.4)},
      inset 0 -18px 20px -20px ${rgba('#2e3032', 0.4)};
  }
`;

const AccordionStyled = styled(Box)`
  &:last-child {
    /* Remove bottom box shadow from last accordion */
    ${AccordionStyledBoxShadowOverlay}::before {
      box-shadow:
        inset 0 18px 20px -20px ${rgba('#2e3032', 0.4)},
        inset 0 -18px 20px -20px ${rgba('#2e3032', 0)};
    }
  }
`;

const BuildingAccordion = props => {
  const {
    id,
    name,
    onBuildingClick,
    selectedBuildingId,
    selectedLevelId,
    onLevelClick,
    resultsNumber,
    levels,
    treeTabIndex,
    treeOnKeyDown,
    isTreeFocused,
    getTreeItemProps,
    focusedItemId,
    T,
  } = props;

  const accordionRef = useRef(null);
  const levelsRef = useRef(null);

  useEffect(() => {
    smoothscroll.polyfill();
    if (levelsRef && levelsRef.current && accordionRef && accordionRef.current) {
      if (accordionRef.current.parentNode.scrollTop > accordionRef.current.offsetTop) {
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else if (
        accordionRef.current.offsetTop -
          accordionRef.current.parentNode.scrollTop +
          accordionRef.current.offsetHeight <=
        accordionRef.current.parentNode.offsetHeight
      ) {
        // fits, do nothing
      } else if (accordionRef.current.offsetHeight > accordionRef.current.parentNode.offsetHeight) {
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }
  }, [selectedBuildingId]);
  // Removing the omitted props below may cause something to fail, lets find out!
  return (
    <AccordionStyled data-testid="BuildingAccordion" ref={accordionRef} display="flex" flexDirection="column">
      <Tooltip text={name} arrowPosition={'left'}>
        <BuildingRow
          id={id}
          name={name}
          selectedBuildingId={selectedBuildingId}
          // Ensure one tabbable root treeitem only (roving tabindex), apply keyboard handler
          onBuildingClick={() => onBuildingClick(id)}
          resultsNumber={resultsNumber}
          ariaExpanded={selectedBuildingId === id}
          ariaControls={selectedBuildingId === id ? `building-${id}-group` : undefined}
          treeTabIndex={treeTabIndex}
          treeOnKeyDown={treeOnKeyDown}
          isTreeFocused={isTreeFocused}
          T={T}
        />
      </Tooltip>

      {selectedBuildingId && selectedBuildingId === id && (
        <AccordionStyledBoxShadowOverlay position="relative">
          <LevelsList
            ref={levelsRef}
            id={`building-${id}-group`}
            role="group"
            aria-label={`${name} levels`}
            levels={levels}
            onLevelClick={onLevelClick}
            selectedLevelId={selectedLevelId}
            buildingName={name}
            getTreeItemProps={getTreeItemProps}
            focusedItemId={focusedItemId}
            T={T}
          />
        </AccordionStyledBoxShadowOverlay>
      )}
    </AccordionStyled>
  );
};

BuildingAccordion.displayName = 'Building Accordion';

export default BuildingAccordion;
